import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';

export default function PartnerNewPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [form, setForm] = useState({
    legal_name: '',
    display_name: '',
    country: 'NL',
    vat_id: '',
    billing_currency: 'EUR',
    primary_contact_name: '',
    primary_email: '',
    primary_phone: '',
    whatsapp_phone: '',
    preferred_language: 'nl',
    status: 'active',
    partner_tier: 'standard',
    internal_notes: '',
  });

  const handleChange = (key: string, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async () => {
    if (!form.legal_name || !form.display_name) {
      setError('Legal name and display name are required');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const id = 'partner-' + crypto.randomUUID().replace(/-/g, '');
      const { error: supaErr } = await supabase.from('partners').insert({
        id,
        legal_name: form.legal_name,
        display_name: form.display_name,
        country: form.country,
        vat_id: form.vat_id || null,
        billing_currency: form.billing_currency,
        primary_contact_name: form.primary_contact_name || null,
        primary_email: form.primary_email || null,
        primary_phone: form.primary_phone || null,
        whatsapp_phone: form.whatsapp_phone || null,
        preferred_language: form.preferred_language,
        status: form.status,
        partner_tier: form.partner_tier,
        internal_notes: form.internal_notes || null,
        reliability_score: 1.0,
      });
      if (supaErr) throw supaErr;
      navigate(`/partners/${id}`);
    } catch (err: any) {
      setError(err?.message || 'Failed to create partner');
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = 'w-full px-3 py-2 text-sm rounded-lg outline-none transition-all';
  const fieldStyle = { background: 'var(--bg-input)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' };
  const labelClass = 'text-[10px] font-medium uppercase tracking-wider block mb-1';
  const labelStyle = { color: 'var(--text-muted)' };

  return (
    <div className="space-y-4 max-w-3xl mx-auto">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/partners')}
          className="p-1.5 rounded-lg transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Create Partner</h1>
      </div>

      {error && (
        <div className="p-3 rounded-xl border text-sm"
          style={{ borderColor: 'var(--danger-border)', background: 'var(--danger-bg)', color: 'var(--danger-text)' }}
        >
          {error}
        </div>
      )}

      <div className="rounded-xl border p-5 space-y-5"
        style={{ background: 'var(--bg-surface-raised)', borderColor: 'var(--border-primary)' }}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className={labelClass} style={labelStyle}>Legal Name *</label>
            <input
              type="text"
              value={form.legal_name}
              onChange={e => handleChange('legal_name', e.target.value)}
              placeholder="Acme Flooring B.V."
              className={fieldClass}
              style={fieldStyle}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Display Name *</label>
            <input
              type="text"
              value={form.display_name}
              onChange={e => handleChange('display_name', e.target.value)}
              placeholder="Acme Flooring"
              className={fieldClass}
              style={fieldStyle}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Country</label>
            <select
              value={form.country}
              onChange={e => handleChange('country', e.target.value)}
              className={fieldClass}
              style={fieldStyle}
            >
              <option value="NL">Netherlands (NL)</option>
              <option value="US">United States (US)</option>
              <option value="DE">Germany (DE)</option>
              <option value="BE">Belgium (BE)</option>
              <option value="UK">United Kingdom (UK)</option>
              <option value="FR">France (FR)</option>
            </select>
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>VAT ID</label>
            <input
              type="text"
              value={form.vat_id}
              onChange={e => handleChange('vat_id', e.target.value)}
              placeholder="NL123456789B01"
              className={fieldClass}
              style={fieldStyle}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Billing Currency</label>
            <select
              value={form.billing_currency}
              onChange={e => handleChange('billing_currency', e.target.value)}
              className={fieldClass}
              style={fieldStyle}
            >
              <option value="EUR">EUR</option>
              <option value="USD">USD</option>
              <option value="GBP">GBP</option>
            </select>
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Partner Tier</label>
            <select
              value={form.partner_tier}
              onChange={e => handleChange('partner_tier', e.target.value)}
              className={fieldClass}
              style={fieldStyle}
            >
              <option value="standard">Standard</option>
              <option value="preferred">Preferred</option>
              <option value="strategic">Strategic</option>
            </select>
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Status</label>
            <select
              value={form.status}
              onChange={e => handleChange('status', e.target.value)}
              className={fieldClass}
              style={fieldStyle}
            >
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="churned">Churned</option>
            </select>
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Primary Contact Name</label>
            <input
              type="text"
              value={form.primary_contact_name}
              onChange={e => handleChange('primary_contact_name', e.target.value)}
              placeholder="John Doe"
              className={fieldClass}
              style={fieldStyle}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Primary Email</label>
            <input
              type="email"
              value={form.primary_email}
              onChange={e => handleChange('primary_email', e.target.value)}
              placeholder="contact@company.com"
              className={fieldClass}
              style={fieldStyle}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Primary Phone</label>
            <input
              type="tel"
              value={form.primary_phone}
              onChange={e => handleChange('primary_phone', e.target.value)}
              placeholder="+31 20 123 4567"
              className={fieldClass}
              style={fieldStyle}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>WhatsApp Phone</label>
            <input
              type="tel"
              value={form.whatsapp_phone}
              onChange={e => handleChange('whatsapp_phone', e.target.value)}
              placeholder="+31 6 12345678"
              className={fieldClass}
              style={fieldStyle}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Preferred Language</label>
            <select
              value={form.preferred_language}
              onChange={e => handleChange('preferred_language', e.target.value)}
              className={fieldClass}
              style={fieldStyle}
            >
              <option value="nl">Dutch</option>
              <option value="en">English</option>
              <option value="de">German</option>
              <option value="fr">French</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelClass} style={labelStyle}>Internal Notes</label>
            <textarea
              value={form.internal_notes}
              onChange={e => handleChange('internal_notes', e.target.value)}
              rows={3}
              placeholder="Any internal notes about this partner..."
              className={fieldClass + ' resize-none'}
              style={fieldStyle}
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={() => navigate('/partners')}
            className="px-4 py-2 text-sm rounded-lg transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl transition-all disabled:opacity-40 whitespace-nowrap"
            style={{ background: 'var(--accent)', color: 'var(--text-inverse)' }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            {saving ? 'Creating...' : 'Create Partner'}
          </button>
        </div>
      </div>
    </div>
  );
}